import { useEffect, useState } from 'react'
import axiosReadInstance from '../../request/axiosReadInstance'
import { useParams } from 'react-router-dom'
import { API_URL } from '@constants'
import { replaceImageExtension } from '@utils'
import { ArticleC } from '@components/article/article'
import { ExpertiseToolbar } from './ExpertiseToolbar'

interface IExpertiseArticle {
    attributes: {
        title: string
        description: string
        client: string
        image: {
            data: {
                attributes: {
                    url: string
                }
            }
        }
        expertise_article_detail: {
            data: {
                id: number
                attributes: {
                    client: string
                }
            }
        }
    }
    id: number
}

export interface IExpertiseCategory {
    attributes: {
        name: string
        expertise_articles: {
            data: IExpertiseArticle[]
        }
    }
    id: number
}

export const ExpertiseArticle: React.FC = () => {
    const { id } = useParams<{ id: string }>()
    const [expertiseCategories, setExpertiseCategories] = useState<
        IExpertiseCategory[]
    >([])
    const [filteredArticles, setFilteredArticles] = useState<
        IExpertiseArticle[]
    >([])
    const [loading, setLoading] = useState<boolean>(false)
    const [activeCategoryId, setActiveCategoryId] = useState<number | null>(
        null
    )

    const fetchCategories = async () => {
        setLoading(true)
        await axiosReadInstance
            .get(
                `${API_URL}/api/expertises-categories?filters[expertises_page][urlSlug][$eq]=${id}&populate=expertise_articles.image&populate=expertise_articles.expertise_article_detail`
            )
            .then((response) => {
                const categories = response.data.data
                setExpertiseCategories(categories)

                const allArticles = categories.flatMap(
                    (category: IExpertiseCategory) =>
                        category.attributes.expertise_articles.data
                )
                setFilteredArticles(allArticles)
            })
            .finally(() => {
                setLoading(false)
            })
    }

    useEffect(() => {
        fetchCategories()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id])

    const handleCategoryClick = (category: IExpertiseCategory) => {
        setFilteredArticles(category.attributes.expertise_articles.data)
        setActiveCategoryId(category.id)
    }

    const handleShowAll = () => {
        const allArticles = expertiseCategories.flatMap(
            (category: IExpertiseCategory) =>
                category.attributes.expertise_articles.data
        )
        setFilteredArticles(allArticles)
        setActiveCategoryId(null)
    }

    return (
        <section className="py-6 px-6 sm:px-4">
            <div className="mx-auto sm:px-24 pb-8 lg:pb-16">
                {loading && (
                    <div className="flex justify-center">
                        <svg
                            aria-hidden="true"
                            className="inline w-12 h-12 mr-2 animate-spin"
                            viewBox="0 0 100 101"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                fill="#e5e7eb"
                            />
                            <path
                                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                fill="#22c55e"
                            />
                        </svg>
                        <span className="sr-only">Loading...</span>
                    </div>
                )}

                {!loading && expertiseCategories && (
                    <>
                        <ExpertiseToolbar
                            categories={expertiseCategories}
                            activeCategoryId={activeCategoryId}
                            onShowAll={handleShowAll}
                            onSelectCategory={handleCategoryClick}
                        />

                        <div
                            id="expertise-articles-grid"
                            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-24"
                        >
                            {filteredArticles.map((article, index) => {
                                const url = `/expertise-article-detail/${id}/${article.attributes.expertise_article_detail.data.id}`

                                return (
                                    <ArticleC
                                        url={url}
                                        title={article.attributes.title}
                                        description={
                                            article.attributes.description
                                        }
                                        srcImg={replaceImageExtension(
                                            article.attributes.image?.data
                                                ?.attributes?.url || ''
                                        )}
                                        subtitle={
                                            article.attributes
                                                .expertise_article_detail.data
                                                .attributes.client
                                        }
                                        altImg={article.attributes.title}
                                        knowMoreText="En savoir plus"
                                        key={index}
                                        linkId={
                                            index === 0
                                                ? 'expertise-article-link-0'
                                                : undefined
                                        }
                                    />
                                )
                            })}
                        </div>
                    </>
                )}
            </div>
        </section>
    )
}
